import {LocationType, useHistory} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {
  mediaTabletAndUp,
  tabletAndUpLinaria,
} from '../../../constants/customMedia';
import {FONT_SIZES} from '../../../constants/typography';
import {ImageType} from '../../../types/Image';
import {ColorLabel} from '../../atoms/ColorLabel/ColorLabel';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {
  Header,
  HeaderTitle,
  HorizontalSlider,
} from '../../molecules/HorizontalSlider/HorizontalSlider';
import {
  SlideControlArrowEnd,
  SlideControlArrowStart,
  SlideControlText,
  SliderControl,
} from '../../molecules/HorizontalSlider/HorizontalSliderControl';
import {ProductImage} from '../../molecules/ProductImage/ProductImage';
import {SHOULD_TRACK_PRODUCT_SELECTION_ADP} from '../ProductColorOptionsGrid/ProductColorOptionsGrid';
import {useProductColorOptionsPreview} from '../ProductColorOptionsPreview/utils/useProductColorOptionsPreview';
import {OptionItem} from './OptionItem';

export interface ProductColorOptionsProps {
  label?: string;
  options: ProductColorOptionProps[];
  disablePreviewImage?: boolean;
  disableRedirect?: boolean;
  setPreviewImage?: (previewImage?: ImageType) => void;
  setImageGridInactive?: (isActive: boolean) => void;
  onProductSelectionSwipe?: VoidFunction;
  'data-testid'?: string;
  shouldUseSquareProductImages?: boolean;
  hideHeader?: boolean;
  isSmallerHeader?: boolean;
  style?: React.CSSProperties;
}

interface ProductColorOptionProps {
  path: string;
  name: string;
  image: ImageType;
  className?: string;
  isActive: boolean;
  isSoldOut?: boolean;
  onClick?: VoidFunction;
  'data-testid'?: string;
}

export const ProductColorOptions: React.FC<
  React.PropsWithChildren<ProductColorOptionsProps>
> = ({
  label,
  options,
  disableRedirect,
  setPreviewImage,
  onProductSelectionSwipe,
  'data-testid': dataTestid,
  shouldUseSquareProductImages,
  hideHeader,
  isSmallerHeader,
  ...rest
}) => {
  const {
    activeColorIndex,
    activeName,
    handleColorOptionMouseEnter,
    handleColorOptionMouseLeave,
  } = useProductColorOptionsPreview({
    colorOptions: options,
    setPreviewImage,
  });

  const history = useHistory();

  const slideCount = 5;

  return (
    <Wrapper
      data-should-use-square-product-images={shouldUseSquareProductImages}
    >
      <StyledHorizontalSlider
        $smallerHeader={isSmallerHeader}
        dataTestId={dataTestid}
        currentOuterIndex={
          slideCount * Math.floor(activeColorIndex / slideCount)
        }
        constrained
        slideCount={slideCount}
        title={
          !hideHeader && label && activeName ? (
            <ColorLabel
              data-testid={dataTestid}
              label={label}
              name={activeName}
            />
          ) : null
        }
        theme="dark"
        disableHeaderControl={hideHeader}
        hideSideButtons
        onIndexChange={onProductSelectionSwipe}
        onMouseLeave={handleColorOptionMouseLeave}
        {...rest}
      >
        {options.map(
          (
            {path, image, isActive, isSoldOut, onClick, name, ...rest},
            index,
          ) => {
            const [pathname, search] = path.split('?');
            const locationWithState = {
              pathname,
              search,
              state: {
                ...(history.location.state as LocationType['state']),
                [SHOULD_TRACK_PRODUCT_SELECTION_ADP]: true,
              },
            } as LocationType;
            return (
              <ItemContainer
                key={path}
                name={name}
                isActive={isActive}
                onMouseEnter={handleColorOptionMouseEnter({
                  isActive,
                  name,
                  image,
                })}
                {...rest}
                data-testid={`${dataTestid}Option${index}`}
                data-is-active={`${isActive}`}
              >
                <ColorOptionItem
                  path={locationWithState}
                  onClick={e => {
                    // Used by the ProductMiniADPDialog to allow the user to
                    // switch between colors without leaving the modal
                    if (disableRedirect) {
                      e.preventDefault();
                    }

                    if (onClick) {
                      onClick();
                    }
                  }}
                  isSoldOut={isSoldOut}
                >
                  <StyledProductImage
                    src={image.url}
                    alt={image.description}
                    padded={image.padded}
                    breakpoints={PRODUCT_IMAGE_BREAKPOINTS}
                  />
                </ColorOptionItem>
              </ItemContainer>
            );
          },
        )}
      </StyledHorizontalSlider>
    </Wrapper>
  );
};

const ColorOptionItem: React.FC<
  React.PropsWithChildren<{
    isSoldOut?: boolean;
    path: LocationType;
    onClick: React.MouseEventHandler;
  }>
> = ({isSoldOut, path, onClick, children}) => {
  return (
    <ColorOptionItemWrapper
      to={path}
      onClick={onClick}
      replace
      forcePushBehaviour
      aria-label={undefined}
    >
      {isSoldOut ? (
        <>
          <SoldOutWrapper data-testid="soldOutColorOption">
            <ProductImageContainer>{children}</ProductImageContainer>
          </SoldOutWrapper>
          <StyledIcon
            data-testid="soldOutColorOptionMailIcon"
            asset={GenericIconName.Mail}
            size={'S'}
            fill={COLORS.Grey1}
          />
        </>
      ) : (
        <>{children}</>
      )}
    </ColorOptionItemWrapper>
  );
};

const ColorOptionItemWrapper = styled(UnstyledLinkLinaria)`
  display: block;
  padding: 5px;
  width: 100%;
  position: relative;

  @media ${mediaTabletAndUp} {
    padding: 8px;
  }

  [data-should-use-square-product-images='true'] & {
    padding: 0;

    @media ${mediaTabletAndUp} {
      padding: 2px;
    }
  }
`;

const ProductImageContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;

  &:after {
    position: absolute;
    content: '';
    display: block;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgb(242, 242, 242, 0.5);
  }
`;

const StyledProductImage = styled(ProductImage)<{
  $shouldUseSquareProductImages?: boolean;
}>`
  [data-should-use-square-product-images='true'] & {
    padding-top: 100% !important;
  }
`;

const SoldOutWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  position: relative;
  cursor: pointer;
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const PRODUCT_IMAGE_BREAKPOINTS = [{width: '60px'}];

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledHorizontalSlider = styled(HorizontalSlider)<{
  $smallerHeader?: boolean;
}>`
  ${SliderControl} {
    display: flex;
  }

  ${Header} {
    margin-bottom: 12px;
    margin-top: 0;

    span {
      font-size: ${props =>
        props.$smallerHeader ? FONT_SIZES.px12 : FONT_SIZES.px14};
    }
  }

  ${HeaderTitle} {
    // header controls are 64px wide; title should take the remaining space with ellipsis overflow.
    width: calc(100% - 64px);
  }

  ${SlideControlText} {
    display: none;
  }

  ${SlideControlArrowStart} {
    padding: 10px 16px;
  }

  ${SlideControlArrowEnd} {
    padding: 10px 16px;
  }

  [data-should-use-square-product-images='true'] & {
    ${Header} {
      margin-bottom: 8px;
    }

    ${SlideControlArrowStart} {
      padding: 5px 16px;
    }

    ${SlideControlArrowEnd} {
      padding: 5px 16px;
    }
  }
`;

const ImageLink = styled(UnstyledLinkLinaria)`
  display: block;
  padding: 5px;
  width: 100%;

  ${tabletAndUpLinaria('padding: 8px;')}
`;

const ItemContainer = styled(OptionItem)`
  display: inline-block;
  border-radius: 3px;
  border: 1.5px solid ${COLORS.Grey4};
  background-color: ${COLORS.Grey4};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 0;
  flex-shrink: 0;
  transition: border-color 200ms ease-in-out;
  overflow: hidden;
  width: 60px;
  aspect-ratio: 3/4;

  &[data-is-active='true'] {
    border-color: ${COLORS.Black};

    ${ImageLink} {
      cursor: default;
    }
  }

  @media (hover: hover) {
    &:hover {
      border-color: ${COLORS.Black};
    }
  }

  [data-should-use-square-product-images='true'] & {
    aspect-ratio: 1/1;
    max-height: 46px;
    max-width: 46px;
  }
`;
